import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/client";

type DeployTarget = "frontend" | "backend" | "crawler" | "all" | "check";

type DeployStatus = {
  status?: "IDLE" | "QUEUED" | "RUNNING" | "SUCCESS" | "FAILED" | string;
  target?: string;
  startedAt?: string;
  finishedAt?: string;
  exitCode?: number | string | null;
  message?: string;
  unitActive?: boolean;
  log?: string;
};

const UI = {
  panel: "#ffffff",
  panelSoft: "#f8fafc",
  border: "#dbe4ee",
  title: "#172033",
  text: "#344054",
  muted: "#667085",
  blue: "#155eef",
  blueSoft: "#eaf2ff",
  green: "#027a48",
  greenSoft: "#ecfdf3",
  red: "#b42318",
  redSoft: "#fef3f2",
  amber: "#b54708",
  amberSoft: "#fffaeb",
  dark: "#101828",
};

const TARGETS: Array<{ key: DeployTarget; label: string; description: string; dangerous?: boolean }> = [
  { key: "frontend", label: "프론트 배포", description: "deploy_frontend.sh" },
  { key: "backend", label: "백엔드 배포", description: "deploy_backend.sh", dangerous: true },
  { key: "crawler", label: "크롤러 배포", description: "deploy_crawler.sh", dangerous: true },
  { key: "all", label: "전체 배포", description: "deploy_all.sh", dangerous: true },
];

function formatDateTime(value?: string) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ko-KR", { hour12: false });
}

function statusMeta(status?: string) {
  switch (status) {
    case "SUCCESS":
      return { label: "성공", color: UI.green, background: UI.greenSoft };
    case "FAILED":
      return { label: "실패", color: UI.red, background: UI.redSoft };
    case "RUNNING":
      return { label: "배포 중", color: UI.blue, background: UI.blueSoft };
    case "QUEUED":
      return { label: "대기 중", color: UI.amber, background: UI.amberSoft };
    default:
      return { label: "대기", color: UI.muted, background: UI.panelSoft };
  }
}

function isTransientDeployHttpStatus(status?: number) {
  return status === 500 || status === 502 || status === 503 || status === 504;
}

export default function AdminDeployPanel() {
  const [status, setStatus] = useState<DeployStatus>({ status: "IDLE" });
  const [loading, setLoading] = useState(true);
  const [actionTarget, setActionTarget] = useState<DeployTarget | null>(null);
  const [error, setError] = useState<string>("");
  const [reconnecting, setReconnecting] = useState(false);
  const deploymentExpectedRef = useRef(false);

  const busy = status.status === "RUNNING" || status.status === "QUEUED" || actionTarget !== null;
  const meta = useMemo(() => statusMeta(status.status), [status.status]);

  const loadStatus = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await api.get<DeployStatus>("/admin/deploy/status", {
        timeout: 5000,
      });
      const next = response.data || { status: "IDLE" };
      setStatus(next);
      setError("");
      setReconnecting(false);

      if (next.status === "RUNNING" || next.status === "QUEUED") {
        deploymentExpectedRef.current = true;
      } else if (next.status === "SUCCESS" || next.status === "FAILED" || next.status === "IDLE") {
        deploymentExpectedRef.current = false;
      }
    } catch (err: any) {
      const httpStatus = err?.response?.status as number | undefined;

      if (httpStatus === 401 || httpStatus === 403) {
        deploymentExpectedRef.current = false;
        setReconnecting(false);
        setError("배포 API 접근 권한이 없습니다. ADMIN 로그인 상태를 확인하세요.");
      } else if (!err?.response || isTransientDeployHttpStatus(httpStatus)) {
        // backend/all 배포 중 Spring이 재기동되는 짧은 구간은 정상적인 연결 단절로 처리한다.
        // 상태를 실패로 덮어쓰지 않고 polling을 유지하여 backend 복귀 후 SUCCESS/FAILED를 다시 읽는다.
        setReconnecting(true);
        setError("");
      } else {
        setReconnecting(false);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "배포 상태 조회에 실패했습니다."
        );
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  const runTarget = useCallback(async (target: DeployTarget) => {
    if (busy) return;

    deploymentExpectedRef.current = true;
    setActionTarget(target);
    setReconnecting(false);
    setError("");

    try {
      const response = await api.post<DeployStatus>(`/admin/deploy/${target}`, undefined, {
        timeout: 10000,
      });
      setStatus(response.data || { status: "QUEUED", target });
    } catch (err: any) {
      const httpStatus = err?.response?.status as number | undefined;

      if (httpStatus === 409) {
        deploymentExpectedRef.current = false;
        setError("이미 다른 배포가 진행 중입니다.");
      } else if (httpStatus === 401 || httpStatus === 403) {
        deploymentExpectedRef.current = false;
        setError("배포 실행 권한이 없습니다. ADMIN 로그인 상태를 확인하세요.");
      } else if (!err?.response || isTransientDeployHttpStatus(httpStatus)) {
        // 요청 직후 backend 연결이 끊긴 경우에도 배포 runner가 이미 시작됐을 수 있다.
        // 실패로 확정하지 않고 상태 polling으로 최종 결과를 회수한다.
        setStatus((previous) => ({
          ...previous,
          status: previous.status === "SUCCESS" || previous.status === "FAILED" ? previous.status : "QUEUED",
          target,
          message: "배포 요청 후 백엔드 연결 확인 중",
        }));
        setReconnecting(true);
        setError("");
      } else {
        deploymentExpectedRef.current = false;
        setError(err?.response?.data?.message || err?.message || "배포 시작에 실패했습니다.");
      }
    } finally {
      setActionTarget(null);
      window.setTimeout(() => void loadStatus(true), 700);
    }
  }, [busy, loadStatus]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const shouldPollFast =
      busy ||
      reconnecting ||
      deploymentExpectedRef.current ||
      status.status === "RUNNING" ||
      status.status === "QUEUED";

    const intervalMs = shouldPollFast ? 2000 : 10000;
    const timer = window.setInterval(() => void loadStatus(true), intervalMs);
    return () => window.clearInterval(timer);
  }, [busy, loadStatus, reconnecting, status.status]);

  return (
    <section
      style={{
        background: UI.panel,
        border: `1px solid ${UI.border}`,
        borderRadius: 14,
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
        marginBottom: 16,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "16px 18px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 14,
          flexWrap: "wrap",
          borderBottom: `1px solid ${UI.border}`,
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
            <strong style={{ color: UI.title, fontSize: 17 }}>운영 원클릭 배포</strong>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: 25,
                padding: "0 9px",
                borderRadius: 999,
                color: reconnecting ? UI.amber : meta.color,
                background: reconnecting ? UI.amberSoft : meta.background,
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              {loading ? "확인 중" : reconnecting ? "재연결 중" : meta.label}
            </span>
          </div>
          <p style={{ margin: "7px 0 0", color: UI.muted, fontSize: 13, lineHeight: 1.55 }}>
            서버의 기존 deploy_*.sh를 실행합니다. 백엔드 재기동 중에는 상태 연결을 자동으로 복구하며 배포 작업은 독립 systemd unit에서 계속됩니다.
          </p>
        </div>

        <button
          type="button"
          disabled={busy}
          onClick={() => void runTarget("check")}
          style={{
            border: `1px solid ${UI.border}`,
            background: UI.panelSoft,
            color: UI.dark,
            borderRadius: 9,
            padding: "8px 12px",
            fontWeight: 800,
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.55 : 1,
          }}
        >
          서비스 상태 확인
        </button>
      </div>

      <div style={{ padding: 18 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 10,
          }}
        >
          {TARGETS.map((item) => {
            const active = actionTarget === item.key || (busy && status.target === item.key);
            return (
              <button
                key={item.key}
                type="button"
                disabled={busy}
                onClick={() => void runTarget(item.key)}
                title={`${item.description} 즉시 실행`}
                style={{
                  minHeight: 72,
                  border: item.dangerous ? "1px solid #f3b7b2" : `1px solid ${UI.border}`,
                  background: active ? UI.blueSoft : item.dangerous ? "#fffafa" : UI.panelSoft,
                  color: item.dangerous ? UI.red : UI.dark,
                  borderRadius: 11,
                  padding: "11px 12px",
                  textAlign: "left",
                  cursor: busy ? "not-allowed" : "pointer",
                  opacity: busy && !active ? 0.48 : 1,
                }}
              >
                <strong style={{ display: "block", fontSize: 14 }}>{active ? "실행 요청 중..." : item.label}</strong>
                <small style={{ display: "block", marginTop: 5, color: UI.muted }}>{item.description}</small>
              </button>
            );
          })}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 8,
            marginTop: 14,
          }}
        >
          {[
            ["대상", status.target || "-"],
            ["시작", formatDateTime(status.startedAt)],
            ["종료", formatDateTime(status.finishedAt)],
            ["결과", reconnecting ? "백엔드 재기동/상태 재연결 중" : status.message || meta.label],
          ].map(([label, value]) => (
            <div key={label} style={{ border: `1px solid ${UI.border}`, borderRadius: 9, padding: "9px 11px" }}>
              <small style={{ color: UI.muted }}>{label}</small>
              <strong style={{ display: "block", marginTop: 4, color: UI.text, fontSize: 13, wordBreak: "break-word" }}>
                {value}
              </strong>
            </div>
          ))}
        </div>

        {reconnecting && (
          <div
            style={{
              marginTop: 12,
              border: "1px solid #fedf89",
              background: UI.amberSoft,
              color: UI.amber,
              borderRadius: 9,
              padding: 10,
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            백엔드 재기동 또는 일시적인 연결 전환 구간입니다. 배포 실패로 판정하지 않고 2초 간격으로 상태를 다시 확인합니다.
          </div>
        )}

        {error && (
          <div style={{ marginTop: 12, border: "1px solid #fecdca", background: UI.redSoft, color: UI.red, borderRadius: 9, padding: 10, fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 7 }}>
            <strong style={{ color: UI.title, fontSize: 13 }}>최근 배포 로그</strong>
            <button
              type="button"
              onClick={() => void loadStatus(true)}
              style={{ border: 0, background: "transparent", color: UI.blue, fontWeight: 800, cursor: "pointer", fontSize: 12 }}
            >
              로그 새로고침
            </button>
          </div>
          <pre
            style={{
              margin: 0,
              minHeight: 120,
              maxHeight: 340,
              overflow: "auto",
              borderRadius: 10,
              background: "#0b1220",
              color: "#d7e2f0",
              padding: 12,
              fontSize: 11,
              lineHeight: 1.55,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {status.log?.trim() || "아직 배포 로그가 없습니다."}
          </pre>
        </div>
      </div>
    </section>
  );
}
